//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import React from 'react';
import styled from 'styled-components';

import ActionButton from '@components/atoms/action-button/ActionButton';
import EmojiPicker from '@components/atoms/emoji-picker/EmojiPicker';
import { BodyText } from '@components/atoms/text/Text';
import { Props as PopoverProps } from '@components/molecules/popover/Popover';
import { useSmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import { insertEmoji } from '@components/molecules/smart-input/emojis/emojiUtils';
import { RecommendedCaptionDropdown } from '@components/molecules/smart-input/recommended-caption/RecommendedCaptionDropdown';
import {
  DropdownSectionType,
  DropdownValue,
} from '@components/molecules/smart-input/smartInputModel';
import { SmartInputTrackingId } from '@components/molecules/smart-input/smartInputTrackingIds';
import {
  getCharAfterSelection,
  getCharBeforeSelection,
  getCurrentStateProperties,
  getSmartInputTrackingId,
  insertText,
  replaceAllText,
} from '@components/molecules/smart-input/smartInputUtils';
import { MediumTooltip } from '@components/molecules/tooltip/Tooltip';
import { DropdownMenu } from '@components/primitives/dropdown-menu/DropdownMenu';
import { IUiaModalClosedResponse } from '@legacy-components/common/universal-image-actions/models/events';
import { formatTrackingId } from '@social-response/utils/format-tracking-id';
import { isDefined } from '@utils/filterPredicates';
import { isNullOrEmpty } from '@utils/stringUtils';
import { getUrlWithoutProtocol } from '@utils/urlUtils';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  emojiConnectPopover: PopoverProps['connectPopover'];
  isDisabled: boolean;
  setIsFocused: (shouldFocus: boolean) => void;
}

export const ACTION_BAR_HEIGHT = 40;

const SINGLE_SPACE = ' ';
const EMPTY_STRING = '';

const getOptionalSpace = (adjacentChar?: string) => {
  return adjacentChar && adjacentChar !== SINGLE_SPACE
    ? SINGLE_SPACE
    : EMPTY_STRING;
};

export const SmartInputActionBar = ({
  className = '',
  emojiConnectPopover,
  isDisabled,
  setIsFocused,
}: Props) => {
  const smartInputContext = useSmartInputContext();

  const getCharacterSurroundingSelection = () => {
    const editorState = smartInputContext.editor.getEditorState();
    const { currentContentState, currentSelectionState } =
      getCurrentStateProperties(editorState);
    const charAfter = getCharAfterSelection(
      currentContentState,
      currentSelectionState,
    );
    const charBefore = getCharBeforeSelection(
      currentContentState,
      currentSelectionState,
    );

    return { charBefore, charAfter };
  };

  const handleShortLinkClick = () => {
    // Close any open plugins
    if (smartInputContext.recommendedHashtags?.isDropdownOpen) {
      smartInputContext.recommendedHashtags.setIsDropdownOpen(false, false);
    }

    if (smartInputContext.mentions?.isDropdownOpen) {
      smartInputContext.mentions.setIsDropdownOpen(false, false);
    }

    const { charAfter, charBefore } = getCharacterSurroundingSelection();

    const optionalPrependedSpace = getOptionalSpace(charBefore);
    const optionalAppendedSpace = getOptionalSpace(charAfter);

    smartInputContext.linkShortening?.openModal({
      insertShortLink: (shortLink: string) => {
        insertText(
          `${optionalPrependedSpace}${shortLink}${optionalAppendedSpace}`,
          smartInputContext,
        );

        return Promise.resolve();
      },
      isEditingLink: false,
    });
  };

  const handleMentionClick = () => {
    const AT_SYMBOL = '@';

    // Close any open plugins
    if (smartInputContext.recommendedHashtags?.isDropdownOpen) {
      smartInputContext.recommendedHashtags.setIsDropdownOpen(false, false);
    }

    if (smartInputContext.mentions?.isDropdownOpen) {
      smartInputContext.mentions.setIsDropdownOpen(false, true);
      return;
    }

    const { charBefore } = getCharacterSurroundingSelection();
    const optionalPrependedSpace = getOptionalSpace(charBefore);

    if (charBefore !== AT_SYMBOL) {
      insertText(`${optionalPrependedSpace}${AT_SYMBOL}`, smartInputContext);
    } else {
      smartInputContext.editor.focusEditor();
    }

    smartInputContext.mentions?.setMentionsSuggestionsQuery({
      blockKey: EMPTY_STRING,
      end: 0,
      queryString: EMPTY_STRING,
      start: 0,
    });
    smartInputContext.mentions?.setIsDropdownOpen(true, false);
  };

  const handleShowroomClick = () => {
    const addShortlinkToCaption = async (res: IUiaModalClosedResponse) => {
      if (
        res.result?.newShowroomData &&
        isDefined(smartInputContext.showroomLinks)
      ) {
        try {
          const showroomData = res.result.newShowroomData;
          const { shortLink } =
            await smartInputContext.showroomLinks.getShowroomShortlink({
              implId: showroomData.impl.id,
              name: showroomData.impl.name,
              sourceId: res.itemId,
              sourceType: 'item',
              instanceId: showroomData.instances?.[0]?.instanceId,
            });

          const { charAfter, charBefore } = getCharacterSurroundingSelection();

          const optionalPrependedSpace = getOptionalSpace(charBefore);
          const optionalAppendedSpace = getOptionalSpace(charAfter);

          if (isDefined(shortLink)) {
            insertText(
              `${optionalPrependedSpace}${getUrlWithoutProtocol(
                shortLink,
              )}${optionalAppendedSpace}`,
              smartInputContext,
            );
          }
        } catch (e) {
          // Fail silently
          return;
        }
      }
    };

    smartInputContext.showroomLinks?.getShowroomLink(addShortlinkToCaption);
  };

  const handleGenerativeAiCaptionClick = () => {
    // Close any open plugins
    if (smartInputContext.recommendedHashtags?.isDropdownOpen) {
      smartInputContext.recommendedHashtags.setIsDropdownOpen(false, false);
    }

    if (smartInputContext.mentions?.isDropdownOpen) {
      smartInputContext.mentions.setIsDropdownOpen(false, false);
    }

    smartInputContext.generativeAiCaption?.openModal();
  };

  const handleGenerativeAiResponseClick = (value: DropdownValue) => {
    smartInputContext.generativeAiResponse
      ?.getGeneratedResponse(value)
      .then((response) => {
        if (isNullOrEmpty(response)) {
          return;
        }
        replaceAllText(response, smartInputContext);
      })
      .catch((e) => {
        // Fail silently
        return;
      });
  };

  const genAiDisabledPopoverContent = (() => {
    if (smartInputContext.generativeAiCaption?.isEnabled) {
      const tooltipContent =
        smartInputContext.generativeAiCaption.disabledTierTooltipContent ??
        'Contact Bazaarvoice Support for more information.';

      return (
        <BodyText>
          {smartInputContext.generativeAiCaption.isEnabledForPlanTier ? (
            'Select an image to auto-generate a caption.'
          ) : (
            <>Upgrade to unlock AI caption generation.&nbsp; {tooltipContent}</>
          )}
        </BodyText>
      );
    } else {
      return <></>;
    }
  })();

  const recommendedHashtagsDisabledPopoverContent = (() => {
    if (
      !smartInputContext.recommendedHashtags
        ?.isRecommendedAiHashtagsEnabledForPlanTier
    ) {
      return (
        <BodyText>
          Upgrade to unlock AI hashtag recommendations.&nbsp;
          {smartInputContext.recommendedHashtags?.disabledTierTooltipContent ??
            'Contact Bazaarvoice Support for more information.'}
        </BodyText>
      );
    }

    return smartInputContext.recommendedHashtags.disabledText;
  })();

  return (
    <Wrapper
      className={className}
      isDisabled={isDisabled}
      onBlur={() => setIsFocused(false)}
      onFocus={() => setIsFocused(true)}
      tabIndex={-1}
    >
      {smartInputContext.emojiPicker?.isEnabled && (
        <EmojiPicker
          appendTarget='body'
          connectPopover={emojiConnectPopover}
          iconColor={theme.colors.gray9}
          isDisabled={isDisabled}
          isStatic={true}
          label='Add Emojis'
          onSelect={(emoji) => insertEmoji(emoji, smartInputContext)}
          onTogglePopover={(_isOpen) => {
            if (smartInputContext.recommendedHashtags?.isDropdownOpen) {
              smartInputContext.recommendedHashtags.setIsDropdownOpen(false);
            }
            if (smartInputContext.mentions?.isDropdownOpen) {
              smartInputContext.mentions.setIsDropdownOpen(false);
            }
          }}
          placement={smartInputContext.emojiPicker.placement}
          size='small'
          trackingId={getSmartInputTrackingId(
            smartInputContext.trackingIdPrefix,
            SmartInputTrackingId.OpenEmojiPickerActionBarButton,
          )}
        />
      )}
      {smartInputContext.recommendedCaption?.isEnabled && (
        <RecommendedCaptionDropdown />
      )}
      {(smartInputContext.recommendedHashtags
        ?.isRecommendedRecentHashtagsEnabled ||
        smartInputContext.recommendedHashtags
          ?.isRecommendedAiHashtagsEnabled) && (
        <ActionButton
          aria-expanded={smartInputContext.recommendedHashtags.isDropdownOpen}
          aria-label={`${
            smartInputContext.recommendedHashtags.isDropdownOpen
              ? 'Close'
              : 'Open'
          } recommended hashtags dropdown`}
          disabledPopoverContent={recommendedHashtagsDisabledPopoverContent}
          iconColor={theme.colors.gray9}
          iconName={
            smartInputContext.recommendedHashtags
              .isRefetchingRecommendedAiHashtags
              ? 'spinner'
              : 'hashtag'
          }
          isDisabled={
            (!!smartInputContext.recommendedHashtags.disabledText &&
              !smartInputContext.recommendedHashtags
                .isRecommendedRecentHashtagsEnabled) ||
            isDisabled ||
            (!!smartInputContext.recommendedHashtags
              .isRecommendedAiHashtagsEnabled &&
              !smartInputContext.recommendedHashtags
                .isRecommendedAiHashtagsEnabledForPlanTier &&
              !smartInputContext.recommendedHashtags
                .isRecommendedRecentHashtagsEnabled)
          }
          label='Recommended Hashtags'
          onClick={() => {
            smartInputContext.recommendedHashtags?.setIsDropdownOpen(
              !smartInputContext.recommendedHashtags.isDropdownOpen,
              true,
            );
          }}
          size='small'
          trackingId={getSmartInputTrackingId(
            smartInputContext.trackingIdPrefix,
            SmartInputTrackingId.OpenRecommendedHashtagsDropdownActionBarButton,
          )}
        />
      )}
      {smartInputContext.generativeAiCaption?.isEnabled && (
        <ActionButton
          aria-label={'Opens dialog to create an auto-generated caption'}
          disabledPopoverContent={genAiDisabledPopoverContent}
          iconColor={theme.colors.gray9}
          iconName='generative-ai-caption'
          isDisabled={
            !smartInputContext.generativeAiCaption.isModalTriggerEnabled ||
            !smartInputContext.generativeAiCaption.isEnabledForPlanTier
          }
          label='Auto-Generate Caption'
          onClick={handleGenerativeAiCaptionClick}
          size='small'
          trackingId={getSmartInputTrackingId(
            smartInputContext.trackingIdPrefix,
            SmartInputTrackingId.GenerativeAiCaptionOpenModalActionBarButton,
          )}
        />
      )}
      {smartInputContext.generativeAiResponse?.isEnabled &&
        isDefined(smartInputContext.generativeAiResponse.dropdownConfig) && (
          <DropdownMenu modal={false}>
            <DropdownMenu.Trigger asChild>
              <ActionButton
                iconName={
                  smartInputContext.generativeAiResponse
                    .isAiGenerativeResponseLoading
                    ? 'spinner'
                    : 'sparkles'
                }
                label='AI Writing Tools'
                size='small'
                trackingId={getSmartInputTrackingId(
                  smartInputContext.trackingIdPrefix,
                  SmartInputTrackingId.GenerativeAiResponseActionBarButton,
                )}
              />
            </DropdownMenu.Trigger>

            <DropdownMenu.Content align='end'>
              {smartInputContext.generativeAiResponse.dropdownConfig.type ===
                DropdownSectionType.STANDARD && (
                <DropdownMenu.Group>
                  {smartInputContext.generativeAiResponse.dropdownConfig.options.map(
                    (option) => (
                      <>
                        {!option.subOptions && (
                          <>
                            {(() => {
                              const trackingId = `${SmartInputTrackingId.GenerativeAiResponseDropdownConfig}-${formatTrackingId(option.label)}`;
                              return (
                                <DropdownMenu.Item
                                  key={option.label}
                                  disabled={
                                    option.canBeDisabled &&
                                    smartInputContext.generativeAiResponse
                                      ?.shouldDisableMenu
                                  }
                                  onClick={
                                    !(
                                      option.canBeDisabled &&
                                      smartInputContext.generativeAiResponse
                                        ?.shouldDisableMenu
                                    )
                                      ? () =>
                                          handleGenerativeAiResponseClick(
                                            option.value,
                                          )
                                      : undefined
                                  }
                                  trackingId={trackingId}
                                >
                                  {!option.canBeDisabled ||
                                  !smartInputContext.generativeAiResponse
                                    ?.shouldDisableMenu ? (
                                    option.label
                                  ) : (
                                    <MediumTooltip
                                      content={option.tooltip}
                                      placement='auto'
                                      target={<>{option.label}</>}
                                    />
                                  )}
                                </DropdownMenu.Item>
                              );
                            })()}
                          </>
                        )}
                        {option.subOptions && option.subOptions.length > 0 && (
                          <DropdownMenu.Sub>
                            <DropdownMenu.SubTrigger
                              disabled={
                                option.canBeDisabled &&
                                smartInputContext.generativeAiResponse
                                  ?.shouldDisableMenu
                              }
                            >
                              {!option.canBeDisabled ||
                              !smartInputContext.generativeAiResponse
                                ?.shouldDisableMenu ? (
                                option.label
                              ) : (
                                <MediumTooltip
                                  content={option.tooltip}
                                  placement='auto'
                                  target={<>{option.label}</>}
                                />
                              )}

                              <DropdownMenu.NavIcon />
                            </DropdownMenu.SubTrigger>
                            <DropdownMenu.SubContent>
                              {option.subOptions.map((subOption) => {
                                const trackingId = `${SmartInputTrackingId.GenerativeAiResponseDropdownConfig}-${formatTrackingId(subOption.label)}`;

                                return (
                                  <DropdownMenu.Item
                                    key={subOption.label}
                                    onClick={() =>
                                      handleGenerativeAiResponseClick(
                                        subOption.value,
                                      )
                                    }
                                    trackingId={trackingId}
                                  >
                                    {subOption.label}
                                  </DropdownMenu.Item>
                                );
                              })}
                            </DropdownMenu.SubContent>
                          </DropdownMenu.Sub>
                        )}
                      </>
                    ),
                  )}
                </DropdownMenu.Group>
              )}
            </DropdownMenu.Content>
          </DropdownMenu>
        )}
      {smartInputContext.linkShortening?.isEnabled && (
        <ActionButton
          aria-label={'Opens dialog to add a short link'}
          iconColor={theme.colors.gray9}
          iconName='link'
          label='Add Short Link'
          onClick={handleShortLinkClick}
          size='small'
          trackingId={getSmartInputTrackingId(
            smartInputContext.trackingIdPrefix,
            SmartInputTrackingId.ShortLinkOpenModalActionBarButton,
          )}
        />
      )}
      {smartInputContext.mentions?.isEnabled && (
        <ActionButton
          aria-expanded={smartInputContext.mentions.isDropdownOpen}
          aria-label={`${
            smartInputContext.mentions.isDropdownOpen ? 'Close' : 'Open'
          } mentions suggestions dropdown`}
          iconColor={theme.colors.gray9}
          iconName='at'
          label='Add Mention'
          onClick={handleMentionClick}
          size='small'
          trackingId={getSmartInputTrackingId(
            smartInputContext.trackingIdPrefix,
            SmartInputTrackingId.MentionsSuggestionsOpenDropdownActionBarButton,
          )}
        />
      )}
      {smartInputContext.showroomLinks?.isEnabled && (
        <ActionButton
          aria-label={'Opens dialog to add a showroom link'}
          iconColor={theme.colors.gray9}
          iconName='product-bag'
          label='Add Showroom Link'
          onClick={handleShowroomClick}
          size='small'
          trackingId={getSmartInputTrackingId(
            smartInputContext.trackingIdPrefix,
            SmartInputTrackingId.ShowroomLinksActionBarButton,
          )}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div<{
  isDisabled: boolean;
}>(
  ({ isDisabled, theme }) => `
  display: flex;
  align-items: center;
  position: relative;
  height: ${ACTION_BAR_HEIGHT}px;
  padding: ${theme.spacings[1]};
  background-color: ${theme.colors.gray0};
  border-top: ${theme.borderWidths[1]} solid ${theme.colors.gray2};
  outline: none;

  & :focus {
    outline: none;
  }

  ${
    isDisabled
      ? `
    cursor: not-allowed;
  `
      : ''
  }
`,
);
